import { useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { AppLayout } from './layouts/AppLayout';
import { AuthLayout } from './layouts/AuthLayout';
import { LoginPage } from './pages/auth/LoginPage';
import { RegisterPage } from './pages/auth/RegisterPage';
import { DashboardPage } from './pages/DashboardPage';
import { ContactsPage } from './pages/ContactsPage';
import { CalendarPage } from './pages/CalendarPage';
import { InboxPage } from './pages/InboxPage';
import { SettingsPage } from './pages/SettingsPage';
import { InvoicesPage } from './pages/InvoicesPage';
import { ProductsPage } from './pages/ProductsPage';
import { ServicesPage } from './pages/ServicesPage';
import { PipelinesPage } from './pages/PipelinesPage';
import { TasksPage } from './pages/TasksPage';
import { ProjectsPage } from './pages/ProjectsPage';
import { WorkflowsPage } from './pages/WorkflowsPage';
import { ReportsPage } from './pages/ReportsPage';
import { MarketingPage } from './pages/MarketingPage';
import { ReputationPage } from './pages/ReputationPage';
import { PagesPage } from './pages/PagesPage';
import { FormsPage } from './pages/FormsPage';
import { BlogPage } from './pages/BlogPage';
import { CoursesPage } from './pages/CoursesPage';
import { LearnPage } from './pages/LearnPage';
import { MembershipsPage } from './pages/MembershipsPage';
import { EsignaturePage } from './pages/EsignaturePage';
import { SignPage } from './pages/SignPage';
import { CommunityPage } from './pages/CommunityPage';
import { VoicePage } from './pages/VoicePage';
import { SocialPage } from './pages/SocialPage';
import { WhitelabelPage } from './pages/WhitelabelPage';
import { PublicBookingPage } from './pages/PublicBookingPage';
import { OnboardingPage } from './pages/OnboardingPage';
import { NotFoundPage } from './pages/NotFoundPage';
import { PortalLogin } from './pages/portal/PortalLogin';
import { PortalLayout } from './pages/portal/PortalLayout';
import { PortalHome, PortalBookings, PortalInvoices, PortalMessages, PortalProfile } from './pages/portal/PortalPages';
import { ReviewWidget } from './pages/embed/ReviewWidget';
import { WebchatWidget } from './pages/embed/WebchatWidget';
import { useAuth } from './lib/auth';

export function App() {
  const checkSession = useAuth((s) => s.checkSession);
  const loading = useAuth((s) => s.loading);

  useEffect(() => {
    checkSession();
  }, [checkSession]);

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <div style={{ fontSize: 'var(--text-base)', color: 'var(--color-text-secondary)' }}>Loading…</div>
      </div>
    );
  }

  return (
    <Routes>
      <Route path="/auth" element={<AuthLayout />}>
        <Route index element={<Navigate to="/auth/login" replace />} />
        <Route path="login" element={<LoginPage />} />
        <Route path="register" element={<RegisterPage />} />
      </Route>

      <Route path="/onboarding" element={<OnboardingPage />} />

      <Route path="/app" element={<AppLayout />}>
        <Route index element={<DashboardPage />} />
        <Route path="contacts" element={<ContactsPage />} />
        <Route path="calendar" element={<CalendarPage />} />
        <Route path="inbox" element={<InboxPage />} />
        <Route path="settings" element={<SettingsPage />} />
        <Route path="invoices" element={<InvoicesPage />} />
        <Route path="products" element={<ProductsPage />} />
        <Route path="services" element={<ServicesPage />} />
        <Route path="pipelines" element={<PipelinesPage />} />
        <Route path="tasks" element={<TasksPage />} />
        <Route path="projects" element={<ProjectsPage />} />
        <Route path="workflows" element={<WorkflowsPage />} />
        <Route path="reports" element={<ReportsPage />} />
        <Route path="marketing" element={<MarketingPage />} />
        <Route path="reputation" element={<ReputationPage />} />
        <Route path="pages" element={<PagesPage />} />
        <Route path="forms" element={<FormsPage />} />
        <Route path="blog" element={<BlogPage />} />
        <Route path="courses" element={<CoursesPage />} />
        <Route path="memberships" element={<MembershipsPage />} />
        <Route path="esignature" element={<EsignaturePage />} />
        <Route path="community" element={<CommunityPage />} />
        <Route path="voice" element={<VoicePage />} />
        <Route path="social" element={<SocialPage />} />
        <Route path="whitelabel" element={<WhitelabelPage />} />
      </Route>

      {/* Public routes */}
      <Route path="/book/:tenantSlug" element={<PublicBookingPage />} />
      <Route path="/learn/:courseSlug" element={<LearnPage />} />
      <Route path="/sign/:token" element={<SignPage />} />
      <Route path="/embed/reviews/:tenantSlug" element={<ReviewWidget />} />
      <Route path="/embed/chat/:tenantSlug" element={<WebchatWidget />} />

      {/* Client portal */}
      <Route path="/portal/login" element={<PortalLogin />} />
      <Route path="/portal" element={<PortalLayout />}>
        <Route index element={<PortalHome />} />
        <Route path="bookings" element={<PortalBookings />} />
        <Route path="invoices" element={<PortalInvoices />} />
        <Route path="messages" element={<PortalMessages />} />
        <Route path="profile" element={<PortalProfile />} />
      </Route>

      <Route path="/" element={<Navigate to="/app" replace />} />
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}
